import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import {
  useLoginGoogleMutation,
  useRequestOtpMutation,
  useVerifyOtpMutation,
} from 'services/login/api';
import { useAuth } from 'services/login/hooks';
import { setCredentials } from 'services/login/slice';
import links from 'utils/links';
import { setLanguage } from 'utils/translation';
import OtpInput from 'components/UI/OtpInput';
import styles from './LoginPage.module.css';

const LoginPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requestOtp] = useRequestOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();
  const [loginGoogle] = useLoginGoogleMutation();
  const [showLoginError, setShowLoginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [code, setCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getErrorMessage = (error: unknown) => {
    if (typeof error === 'object' && error !== null) {
      const err = error as { status?: number; data?: { error?: string } };
      if (err.status === 404) {
        return t('noAccountFound');
      }
      if (err.status === 401) {
        return t('invalidOtp');
      }
      if (err.data?.error) {
        return err.data.error;
      }
    }
    return t('invalidConnection');
  };

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, []);

  useEffect(() => {
    if (auth.user) {
      if (auth.user.language) {
        setLanguage(auth.user.language);
      }
      navigate(location.state ? location.state.from : links.paths.home);
    }
  }, [auth]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    const { credential } = credentialResponse;
    if (!credential) {
      setLoginErrorMessage(t('invalidConnection'));
      setShowLoginError(true);
      return;
    }
    try {
      const user = await loginGoogle({ credential }).unwrap();
      if (!user) {
        throw new Error('Login failed');
      }
      dispatch(setCredentials({ user }));
      navigate(location.state ? location.state.from : links.paths.home);
    } catch (error) {
      setLoginErrorMessage(getErrorMessage(error));
      setShowLoginError(true);
    }
  };

  const handleGoogleError = () => {
    setLoginErrorMessage(t('invalidConnection'));
    setShowLoginError(true);
  };

  const handleRequestOtp = async () => {
    const normalizedEmail = emailAddress.trim().toLowerCase();
    if (!normalizedEmail) {
      setLoginErrorMessage(t('invalidConnection'));
      setShowLoginError(true);
      return;
    }
    setEmailAddress(normalizedEmail);
    setIsSubmitting(true);
    setShowLoginError(false);
    try {
      await requestOtp({
        emailAddress: normalizedEmail,
        purpose: 'login',
      }).unwrap();
      setOtpSent(true);
    } catch (error) {
      setLoginErrorMessage(getErrorMessage(error));
      setShowLoginError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    const normalizedEmail = emailAddress.trim().toLowerCase();
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedEmail || !normalizedCode) {
      setLoginErrorMessage(t('invalidConnection'));
      setShowLoginError(true);
      return;
    }
    setIsSubmitting(true);
    setShowLoginError(false);
    try {
      const user = await verifyOtp({
        emailAddress: normalizedEmail,
        code: normalizedCode,
      }).unwrap();
      if (!user) {
        throw new Error('Login failed');
      }
      dispatch(setCredentials({ user }));
      navigate(location.state ? location.state.from : links.paths.home);
    } catch (error) {
      setLoginErrorMessage(getErrorMessage(error));
      setShowLoginError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    if (otpSent) {
      handleVerifyOtp();
    } else {
      handleRequestOtp();
    }
  };

  return (
    <div className={styles.Login}>
      <Helmet>
        <title>{t('login')}</title>
      </Helmet>
      <h4>{t('login')}</h4>
      <div className={styles.layout}>
        {
          showLoginError && (
            <Alert variant="danger" >
              <strong>{loginErrorMessage || t('invalidConnection')}</strong>
            </Alert>
          )
        }
        {
          otpSent && !showLoginError && (
            <Alert variant="success">
              {t('otpSent')}
            </Alert>
          )
        }
        <div className={styles.columns}>
          <div className={styles.column}>
            <form id="Login_form" onSubmit={handleSubmit}>
              <div>
                <Form.Control
                  autoFocus
                  disabled={otpSent}
                  name="emailAddress"
                  onChange={e => setEmailAddress(e.target.value)}
                  placeholder={t('emailAddress')}
                  style={{ marginBottom: 10 }}
                  type="email"
                  value={emailAddress}
                />
              </div>
              {
                otpSent && (
                  <div>
                    <OtpInput
                      autoFocus
                      disabled={isSubmitting}
                      onChange={setCode}
                      value={code}
                    />
                  </div>
                )
              }
              <Button
                disabled={isSubmitting}
                type="submit"
                variant="primary"
              >
                {otpSent ? t('verifyCode') : t('sendCode')}
              </Button>
              {
                otpSent && (
                  <>
                    &nbsp;
                    <Button
                      disabled={isSubmitting}
                      onClick={handleRequestOtp}
                      type="button"
                      variant="link"
                    >
                      {t('resendCode')}
                    </Button>
                  </>
                )
              }
            </form>
            <div style={{ marginTop: 10 }}>
              {t('or')}
              &nbsp;
              <Link to={links.paths.signup}>
                {t('createAnAccount')}
              </Link>
            </div>
          </div>
          <div className={styles.column}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
